import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Modal,
    Paper,
    Tab,
    Tabs,
    TextField,
} from '@material-ui/core';

import { growthApi as api } from 'api/growth';
import { DataTable, FormComponent, Notifications, TabPanel } from 'shared';

function CategoriesComponent({ categories, getCategories }) {
    const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
    const [notification, setNotification] = useState({ text: null, level: null });

    const createCategory = async (category) => {
        const res = await api.createNPSCategory(category);
        if (res.status === true) {
            getCategories();
        } else {
            setNotification({ text: 'Failed to create category', level: 'error' });
        }
    };

    const columns = [
        { accessor: 'id', Header: 'id', editable: false },
        { accessor: 'category', Header: 'Category', editable: false },
    ];

    return (
        <Grid container>
            <Notifications options={notification} />
            {addCategoryModalOpen === true && (
                <AddCategoryModal createCategory={createCategory} setAddCategoryModalOpen={setAddCategoryModalOpen} />
            )}
            <DataTable
                columns={columns}
                data={categories}
                toolbarActions={[
                    {
                        name: 'Add Category',
                        action: () => {
                            setAddCategoryModalOpen(true);
                        },
                    },
                ]}
            />
        </Grid>
    );
}

function AddCategoryModal({ createCategory, setAddCategoryModalOpen }) {
    const [category, setCategory] = useState('');
    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open
            onClose={() => {
                setAddCategoryModalOpen(false);
            }}
        >
            <DialogTitle>Add New Category</DialogTitle>
            <DialogContent>
                <TextField
                    label="Category"
                    value={category}
                    onChange={(event) => {
                        setCategory(event.target.value);
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={async () => {
                        await createCategory(category);
                        setAddCategoryModalOpen(false);
                    }}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function AddSentimentCategoryModal({ categories, closeModal, npsID, setNotification }) {
    const formFields = [
        {
            name: 'sentiment',
            inputElement: 'select',
            label: 'Sentiment',
            gridValue: 4,
            inputProps: { opts: ['POSITIVE', 'NEGATIVE', 'NEUTRAL'], required: true },
        },
        {
            name: 'category',
            inputElement: 'select',
            label: 'Category',
            gridValue: 6,
            inputProps: { opts: categories, required: true },
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const res = await api.createNPSSentCatCombo(npsID, formData);

                        if (!res.status) {
                            setNotification({
                                text: `Failed to add sentiment/category combo. ${res.msg}`,
                                level: 'error',
                            });
                            return false;
                        }

                        closeModal();
                    }}
                />
            </div>
        </Modal>
    );
}

function SentimentCategoryTable({ categories, parentRow }) {
    const [rows, setRows] = useState([]);
    const [npsID, setNpsID] = useState(parentRow.original.id);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [addSentCatModalOpen, setAddSentCatModalOpen] = useState(false);

    // get sentiment and categories for expandable rows
    const getSentimentCategories = async () => {
        const response = await api.getNPSSentimentCategories(npsID);
        if (response.status === false) {
            setNotification({ text: 'Failed to get category/sentiment combos', level: 'error' });
            return;
        }
        setRows(response.data.rows);
    };

    const columns = [
        {
            Header: 'Sentiment',
            accessor: 'sentiment',
            editable: true,
            editProps: {
                type: 'select',
                options: ['POSITIVE', 'NEGATIVE', 'NEUTRAL'],
            },
        },
        {
            Header: 'Category',
            accessor: 'category',
            editable: true,
            width: '350px',
            editProps: {
                type: 'select',
                options: categories,
            },
        },
        {
            accessor: 'remove',
            Header: 'Delete',
            Cell: ({ row }) => (
                <Button
                    onClick={async () => {
                        const response = await api.deleteSentCatCombo(row.original.id);

                        if (response.status === false) {
                            setNotification({ text: `Can't delete item ${response.msg}`, level: 'error' });
                            return;
                        }

                        setNotification({ text: 'Sentiment/Category combo deleted.', level: 'success' });
                        getSentimentCategories();
                    }}
                >
                    Delete
                </Button>
            ),
        },
    ];

    useEffect(() => {
        getSentimentCategories();
    }, []);

    return (
        <div className="batch-table">
            <Notifications options={notification} />
            {addSentCatModalOpen && (
                <AddSentimentCategoryModal
                    npsID={npsID}
                    categories={categories}
                    setNotification={setNotification}
                    closeModal={() => {
                        setAddSentCatModalOpen(false);
                        getSentimentCategories();
                    }}
                />
            )}
            <div>
                <DataTable
                    columns={columns}
                    data={rows}
                    editable
                    saveEdit={async ({ field, id, row, value }) => {
                        const data = { [field]: value };

                        if ([field] == 'sentiment') {
                            data.unchangedVal = row.category;
                        } else {
                            data.unchangedVal = row.sentiment;
                        }

                        const res = await api.updateNPSResult(npsID, id, data);
                        if (!res.status) {
                            setNotification({ text: `Failed to update. ${res.msg}`, level: 'error' });
                            return false;
                        }

                        setNotification({ text: 'Sentiment/Category combo updated.', level: 'success' });
                        getSentimentCategories();
                        return true;
                    }}
                />
            </div>
            <Button
                onClick={() => {
                    setAddSentCatModalOpen(true);
                }}
            >
                Add Line
            </Button>
        </div>
    );
}

function NPSScores({ categories }) {
    const [npsRows, setNpsRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });

    const getNPSResults = async () => {
        const res = await api.getNPSResults();
        if (!res.status) {
            setNotification({ text: 'Failed to get nps results', level: 'error' });
            return;
        }

        setNpsRows(res.data.rows);
    };

    useEffect(() => {
        getNPSResults();
    }, []);

    const columns = [
        { Header: 'ID', accessor: 'id' },
        { Header: 'Score', accessor: 'npsScore' },
        { Header: 'Reason', accessor: 'reason' },
    ];

    return (
        <>
            <Notifications options={notification} />
            <DataTable
                data={npsRows}
                columns={columns}
                expandable
                ExpansionComponent={
                    <SentimentCategoryTable setNotification={setNotification} categories={categories} />
                }
            />
        </>
    );
}

function NPSResults() {
    const [categories, setCategories] = useState([]);
    const [selectedTab, setSelectedTab] = useState('0');
    const [categoryOptions, setCategoryOptions] = useState([]);

    const getCategories = async () => {
        const res = await api.getNPSCategories();
        if (res.status === true) {
            setCategories(res.data.rows);
            setCategoryOptions(res.data.rows.map((element) => element.category));
        }
    };

    useEffect(() => {
        getCategories();
    }, []);

    const handleChange = (event, value) => {
        setSelectedTab(value);
    };

    return (
        <Paper>
            <Tabs onChange={handleChange} value={selectedTab} indicatorColor="primary" textColor="primary" centered>
                <Tab label="NPS Scores" value="0" />
                <Tab label="Categories" value="1" />
            </Tabs>
            <TabPanel selectedTab={selectedTab} index="0">
                <NPSScores categories={categoryOptions} getCategories={getCategories} />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="1">
                <CategoriesComponent categories={categories} getCategories={getCategories} />
            </TabPanel>
        </Paper>
    );
}

export { NPSResults };
